// Node-safe: importa só de ./locales (sem disparar i18n.init()).
import { I18N_ROUTING_ENABLED, SITE_URL, type AppLocale } from './locales';

/**
 * Estratégia de URL (Release C):
 * - PT-BR: SEM prefixo (URLs atuais preservadas → zero 301, SEO intacto)
 * - EN: prefixo /en + slug traduzido
 * - ES: prefixo /es + slug traduzido
 *
 * `ROUTE_SLUGS` mapeia um id estável de rota → slug por idioma. Cobre todas
 * as páginas públicas de rota fixa (home, landings, institucionais, blog).
 * Os posts do blog NÃO entram aqui — o slug traduzido de cada post vem do
 * registry de posts por locale (`content/blog`, Fase 4).
 */
export const ROUTE_SLUGS: Record<string, Record<AppLocale, string>> = {
  // Home — slug vazio (raiz). Em EN/ES vira /en e /es.
  home: { 'pt-BR': '', en: '', es: '' },

  // ── Competições ──────────────────────────────────────────────────────────
  'pool-copa-do-mundo': {
    'pt-BR': 'bolao-copa-do-mundo', en: 'world-cup-pool', es: 'quiniela-mundial',
  },
  'pool-brasileirao': {
    'pt-BR': 'bolao-brasileirao', en: 'brazilian-league-pool', es: 'quiniela-brasileira',
  },
  'pool-copa-do-brasil': {
    'pt-BR': 'bolao-copa-do-brasil', en: 'copa-do-brasil-pool', es: 'quiniela-copa-de-brasil',
  },
  'pool-libertadores': {
    'pt-BR': 'bolao-libertadores', en: 'copa-libertadores-pool', es: 'quiniela-libertadores',
  },
  'pool-champions-league': {
    'pt-BR': 'bolao-champions-league', en: 'champions-league-pool', es: 'quiniela-champions-league',
  },
  'pool-carioca': {
    'pt-BR': 'bolao-carioca', en: 'rio-state-league-pool', es: 'quiniela-carioca',
  },
  'pool-paulista': {
    'pt-BR': 'bolao-paulista', en: 'sao-paulo-state-league-pool', es: 'quiniela-paulista',
  },
  'pool-estaduais': {
    'pt-BR': 'bolao-estaduais', en: 'brazilian-state-leagues-pool', es: 'quiniela-estatales-brasil',
  },

  // ── Simuladores ──────────────────────────────────────────────────────────
  'sim-copa-do-mundo': {
    'pt-BR': 'simulador-copa-do-mundo-2026', en: 'world-cup-2026-simulator', es: 'simulador-mundial-2026',
  },
  'sim-libertadores': {
    'pt-BR': 'simulador-libertadores-2026', en: 'copa-libertadores-2026-simulator', es: 'simulador-libertadores-2026',
  },
  'sim-sudamericana': {
    'pt-BR': 'simulador-copa-sudamericana-2026', en: 'copa-sudamericana-2026-simulator', es: 'simulador-sudamericana-2026',
  },

  // ── Ferramentas ──────────────────────────────────────────────────────────
  'tool-escalar-selecao': {
    'pt-BR': 'escalar-selecao-brasileira-2026', en: 'brazil-squad-builder-2026', es: 'alineacion-seleccion-brasil-2026',
  },
  'tool-escale-seu-time': {
    'pt-BR': 'escale-seu-time', en: 'build-your-team', es: 'arma-tu-equipo',
  },

  // ── Institucionais ───────────────────────────────────────────────────────
  sobre: { 'pt-BR': 'sobre', en: 'about', es: 'acerca' },
  contato: { 'pt-BR': 'contato', en: 'contact', es: 'contacto' },
  'como-jogar': { 'pt-BR': 'como-jogar', en: 'how-to-play', es: 'como-jugar' },
  glossario: { 'pt-BR': 'glossario', en: 'glossary', es: 'glosario' },
  premium: { 'pt-BR': 'premium', en: 'premium', es: 'premium' },
  precos: { 'pt-BR': 'precos', en: 'pricing', es: 'precios' },

  // ── Jurídicas ────────────────────────────────────────────────────────────
  'termos-de-uso': { 'pt-BR': 'termos-de-uso', en: 'terms-of-use', es: 'terminos-de-uso' },
  privacidade: { 'pt-BR': 'privacidade', en: 'privacy-policy', es: 'privacidad' },
  regulamento: { 'pt-BR': 'regulamento', en: 'rules', es: 'reglamento' },
  'excluir-conta': { 'pt-BR': 'excluir-conta', en: 'delete-account', es: 'eliminar-cuenta' },

  // ── Blog (listagem) ──────────────────────────────────────────────────────
  blog: { 'pt-BR': 'blog', en: 'blog', es: 'blog' },

  // ── Área autenticada (Sessão F: i18n da área logada) ────────────────────
  // Rotas SPA dinâmicas (atrás de auth). Não têm prerender — o fallback
  // /index.html da CloudFront Function já cobre. Aqui só registramos slugs
  // por idioma para que LocalizedLink, useLocalizedNavigate e LangSwitcher
  // saibam reescrever as URLs ao trocar de idioma.
  boloes: { 'pt-BR': 'boloes', en: 'pools', es: 'quinielas' },
  'jogos-hoje': { 'pt-BR': 'jogos-hoje', en: 'today', es: 'hoy' },
  'boloes-publicos': { 'pt-BR': 'boloes-publicos', en: 'public-pools', es: 'quinielas-publicas' },
  'ranking-global': { 'pt-BR': 'ranking-global', en: 'global-ranking', es: 'clasificacion-global' },
  'minhas-conquistas': { 'pt-BR': 'minhas-conquistas', en: 'my-achievements', es: 'mis-logros' },
  estatisticas: { 'pt-BR': 'estatisticas', en: 'my-statistics', es: 'mis-estadisticas' },
  notificacoes: { 'pt-BR': 'notificacoes', en: 'notifications', es: 'notificaciones' },
  'minha-conta': { 'pt-BR': 'minha-conta', en: 'my-account', es: 'mi-cuenta' },
  assinatura: { 'pt-BR': 'assinatura', en: 'subscription', es: 'suscripcion' },
  cupons: { 'pt-BR': 'cupons', en: 'coupons', es: 'cupones' },
  jogadores: { 'pt-BR': 'jogadores', en: 'players', es: 'jugadores' },
  usuarios: { 'pt-BR': 'usuarios', en: 'users', es: 'usuarios' },
  configuracoes: { 'pt-BR': 'configuracoes', en: 'settings', es: 'configuracion' },
  'notificar-usuarios': { 'pt-BR': 'notificar-usuarios', en: 'notify-users', es: 'notificar-usuarios' },

  // Auth & onboarding
  auth: { 'pt-BR': 'auth', en: 'auth', es: 'auth' },
  'completar-registro': { 'pt-BR': 'completar-registro', en: 'complete-registration', es: 'completar-registro' },
  'verificar-email': { 'pt-BR': 'verificar-email', en: 'verify-email', es: 'verificar-email' },
  'ativar-conta': { 'pt-BR': 'ativar-conta', en: 'activate-account', es: 'activar-cuenta' },
  'forgot-password': { 'pt-BR': 'forgot-password', en: 'forgot-password', es: 'olvide-contrasena' },
  'reset-password': { 'pt-BR': 'reset-password', en: 'reset-password', es: 'restablecer-contrasena' },
  join: { 'pt-BR': 'join', en: 'join', es: 'unirse' },

  // ── Sub-segmentos de rotas dinâmicas ────────────────────────────────────
  // Rotas como /pools/:poolId/matches têm slug em 2 partes: a base ("pools")
  // e o sufixo ("matches"). Cada parte vira um id próprio para que o
  // LocalizedLink consiga traduzir caminhos multi-segmento preservando o ID
  // dinâmico (numérico) no meio. Ex.: /pools/123/matches (PT) →
  // /en/pools/123/predictions (EN) → /es/quinielas/123/pronosticos (ES).
  'pools-base': { 'pt-BR': 'pools', en: 'pools', es: 'quinielas' },
  'pools-sub-matches': { 'pt-BR': 'matches', en: 'predictions', es: 'pronosticos' },
  'pools-sub-rankings': { 'pt-BR': 'rankings', en: 'ranking', es: 'clasificacion' },
  'competicoes-base': { 'pt-BR': 'competicoes', en: 'competitions', es: 'competiciones' },
  'competicoes-sub-partidas': { 'pt-BR': 'partidas', en: 'matches', es: 'partidos' },
  'admin-base': { 'pt-BR': 'admin', en: 'admin', es: 'admin' },
  'admin-sub-notificacoes': { 'pt-BR': 'notificacoes', en: 'notifications', es: 'notificaciones' },
  'admin-sub-assinaturas': { 'pt-BR': 'assinaturas', en: 'subscriptions', es: 'suscripciones' },
  'admin-sub-times': { 'pt-BR': 'times', en: 'teams', es: 'equipos' },
  'admin-sub-traducoes': { 'pt-BR': 'traducoes', en: 'translations', es: 'traducciones' },
  'admin-sub-webhooks': { 'pt-BR': 'webhooks', en: 'webhooks', es: 'webhooks' },
};

/**
 * Conjunto de routeIds que representam rotas da área autenticada (SPA pós-login).
 * Usado pelo App.tsx para gerar as variantes /en/<slug>, /es/<slug> apontando
 * pros mesmos componentes das rotas PT-BR.
 *
 * NÃO inclui ids base/sub (pools-base, pools-sub-*, competicoes-*, admin-*)
 * porque esses não são rotas standalone — são pedaços de paths dinâmicos que
 * são montados manualmente no roteamento.
 */
export const AUTHENTICATED_ROUTE_IDS: ReadonlyArray<string> = [
  'boloes',
  'jogos-hoje',
  'boloes-publicos',
  'ranking-global',
  'minhas-conquistas',
  'estatisticas',
  'notificacoes',
  'minha-conta',
  'assinatura',
  'cupons',
  'jogadores',
  'usuarios',
  'configuracoes',
  'notificar-usuarios',
  'auth',
  'completar-registro',
  'verificar-email',
  'ativar-conta',
  'forgot-password',
  'reset-password',
  'join',
];

/**
 * Compat: as 8 landings de competição (consumido por código pré-Fase-0).
 * Subconjunto de ROUTE_SLUGS — mantido pra não quebrar imports existentes.
 */
export const LANDING_SLUGS: Record<string, Record<AppLocale, string>> = Object.fromEntries(
  Object.entries(ROUTE_SLUGS).filter(([id]) => id.startsWith('pool-')),
);

/**
 * Índice reverso por locale: slug → routeId. Usado pela resolução de rota.
 *
 * Precedência: ids top-level (ex: `boloes`) ganham sobre ids dinâmicos
 * `-base` (ex: `pools-base`) quando o mesmo slug aparece em ambos. Em EN
 * `boloes` e `pools-base` compartilham slug 'pools' — o usuário esperando
 * trocar idioma em `/en/pools` quer ir pra `/boloes` (PT), não pra `/pools`
 * (rota não registrada no React Router, cai no catch-all e redireciona pra
 * raiz — em `dev.app.*` ainda dispara 301 do CloudFront pro apex).
 * Bug reportado em DEV 2026-06-01.
 */
const SLUG_TO_ID: Record<AppLocale, Record<string, string>> = {
  'pt-BR': {}, en: {}, es: {},
};
// 1ª passada: ids dinâmicos `-base` ganham slot.
// 2ª passada: ids top-level sobrescrevem.
const ENTRIES_BY_PRIORITY = Object.entries(ROUTE_SLUGS).sort(([a], [b]) => {
  const aBase = a.endsWith('-base') ? 0 : 1;
  const bBase = b.endsWith('-base') ? 0 : 1;
  return aBase - bBase;
});
for (const [id, byLocale] of ENTRIES_BY_PRIORITY) {
  (['pt-BR', 'en', 'es'] as AppLocale[]).forEach((loc) => {
    SLUG_TO_ID[loc][byLocale[loc]] = id;
  });
}

/** Prefixo de URL do idioma ('' pra pt-BR, '/en', '/es'). */
export function localePrefix(locale: AppLocale): string {
  return locale === 'pt-BR' ? '' : `/${locale}`;
}

/**
 * Caminho de uma rota num idioma (ex: localizedPath('sobre','en') → /en/about).
 *
 * Com o roteamento i18n desligado (flag `I18N_ROUTING_ENABLED` off) só existem
 * as rotas PT-BR sem prefixo — gerar `/en|/es` levaria o link ao catch-all `*`
 * e redirecionaria pra home. Nesse modo, sempre devolve o caminho PT-BR.
 */
export function localizedPath(routeId: string, locale: AppLocale): string {
  const effective: AppLocale = I18N_ROUTING_ENABLED ? locale : 'pt-BR';
  const slug = ROUTE_SLUGS[routeId]?.[effective];
  if (slug === undefined) return effective === 'pt-BR' ? '/' : `/${effective}`;
  const prefix = localePrefix(effective);
  return slug === '' ? (prefix || '/') : `${prefix}/${slug}`;
}

/** Resolve um slug (sem prefixo de locale) → routeId, dado o locale. */
export function routeIdFromSlug(slug: string, locale: AppLocale): string | undefined {
  return SLUG_TO_ID[locale]?.[slug];
}

/** hreflang entries (pt-BR, en, es + x-default→pt-BR) p/ o componente SEO. */
export function alternatePaths(
  routeId: string,
): Array<{ hreflang: string; href: string }> {
  const locales: AppLocale[] = ['pt-BR', 'en', 'es'];
  const entries = locales.map((l) => ({
    hreflang: l as string,
    href: `${SITE_URL}${localizedPath(routeId, l)}`,
  }));
  entries.push({
    hreflang: 'x-default',
    href: `${SITE_URL}${localizedPath(routeId, 'pt-BR')}`,
  });
  return entries;
}

/** id → todos os routeIds (usado pelo prerender e geração de sitemap). */
export const ALL_ROUTE_IDS = Object.keys(ROUTE_SLUGS);
/** Compat com código que importava ALL_LANDING_IDS (só competições). */
export const ALL_LANDING_IDS = Object.keys(LANDING_SLUGS);

/**
 * Mapas de tradução para sub-segmentos de rotas dinâmicas. Permitem reescrever
 * `/pools/123/matches` (PT) → `/en/pools/123/predictions` preservando o ID
 * numérico no meio. Cada entrada lista os sub-segmentos válidos para uma base.
 *
 * Importante: o `localizePath()` faz lookup por slug+locale, então definimos a
 * tabela uma única vez aqui derivando dos ids `*-base` e `*-sub-*`. Adicionar
 * uma rota dinâmica nova = só registrar os ids correspondentes em ROUTE_SLUGS.
 */
type SubSegmentMap = Record<string, string>; // sub-slug-no-locale → routeId

function buildSubMap(prefix: string): Record<AppLocale, SubSegmentMap> {
  const result: Record<AppLocale, SubSegmentMap> = { 'pt-BR': {}, en: {}, es: {} };
  for (const [id, byLocale] of Object.entries(ROUTE_SLUGS)) {
    if (!id.startsWith(prefix)) continue;
    (['pt-BR', 'en', 'es'] as AppLocale[]).forEach((loc) => {
      result[loc][byLocale[loc]] = id;
    });
  }
  return result;
}

/** Bases multi-segmento conhecidas. Chave = slug PT da base. */
const DYNAMIC_BASES: Record<string, { baseId: string; subPrefix: string }> = {
  pools: { baseId: 'pools-base', subPrefix: 'pools-sub-' },
  competicoes: { baseId: 'competicoes-base', subPrefix: 'competicoes-sub-' },
  admin: { baseId: 'admin-base', subPrefix: 'admin-sub-' },
};

const DYNAMIC_SUB_MAPS: Record<string, Record<AppLocale, SubSegmentMap>> = {
  'pools-sub-': buildSubMap('pools-sub-'),
  'competicoes-sub-': buildSubMap('competicoes-sub-'),
  'admin-sub-': buildSubMap('admin-sub-'),
};

/**
 * Verifica se uma `pathname` corrente (do `useLocation()`) corresponde ao
 * caminho `target` no idioma PT-BR — independente do idioma atualmente exibido
 * na URL. Ex.: `matchesLocalizedPath('/en/pools', '/boloes')` → true.
 *
 * Útil pra `isActive` em menus que comparam com slugs PT-BR mas o user pode
 * estar navegando em `/en/...` ou `/es/...`.
 */
export function matchesLocalizedPath(pathname: string, ptTarget: string): boolean {
  if (pathname === ptTarget) return true;
  if (!I18N_ROUTING_ENABLED) return false;
  // Strip prefix /en|/es do pathname pra comparar com PT canônico
  const m = pathname.match(/^\/(en|es)(\/.*)?$/);
  if (!m) return false;
  const localePart = m[1] as AppLocale;
  const rest = m[2] ?? '/';
  // Tenta traduzir o ptTarget para o locale do path corrente e comparar
  const expected = translatePath(ptTarget, localePart);
  if (expected && expected === pathname) return true;
  // Tenta o caminho inverso: pathname (no locale) volta pra PT?
  const ptEquivalent = translatePath(pathname, 'pt-BR');
  return ptEquivalent === ptTarget || (`/${rest.replace(/^\//, '')}` === ptTarget);
}

/**
 * Traduz um caminho completo (ex.: `/pools/123/matches`) para o slug
 * equivalente no `locale` alvo (ex.: `/en/pools/123/predictions`).
 *
 * Estratégia:
 *   1. Strip do prefix de locale (`/en|/es`) se já houver — assume slug PT-BR
 *      como fonte canônica (idêntico ao que LocalizedLink fazia antes).
 *   2. Quebra em segmentos. Pra cada segmento, se for um slug conhecido,
 *      traduz; segmentos não-mapeados (IDs numéricos, sufixos arbitrários)
 *      passam intactos.
 *   3. Reagrupa preservando query/hash e aplica o prefix do locale alvo.
 *
 * Retorna `null` quando o path não casa nenhuma rota conhecida — caller decide
 * o fallback (LocalizedLink usa prepend simples de `/${locale}` nesse caso).
 */
export function translatePath(rawPath: string, locale: AppLocale): string | null {
  if (!I18N_ROUTING_ENABLED) return rawPath;
  // Separa path / query / hash
  const m = rawPath.match(/^([^?#]*)([?#].*)?$/);
  if (!m) return null;
  const pathOnly = m[1];
  const tail = m[2] ?? '';

  // Detecta idioma fonte pelo prefix do path (não assume pt-BR).
  // Bug observado em DEV 2026-06-02: /en/admin/subscriptions trocava idioma
  // pra PT e ia pro apex porque o lookup subMap['pt-BR']['subscriptions']
  // dava undefined (chave PT-BR é 'assinaturas'). Mesmo problema em
  // /en/pools/{id}/predictions, /en/competitions/{id}/matches etc.
  let work = pathOnly;
  let sourceLocale: AppLocale = 'pt-BR';
  const prefixMatch = work.match(/^\/(en|es)(\/|$)/);
  if (prefixMatch) {
    sourceLocale = prefixMatch[1] as AppLocale;
    work = work.slice(3); // remove /en ou /es
    if (work === '') work = '/';
  }

  const segments = work.split('/').filter((s) => s.length > 0);
  if (segments.length === 0) {
    // Home
    const prefix = localePrefix(locale);
    return `${prefix || '/'}${tail}`;
  }

  // Primeiro segmento: ou é slug top-level (ex: 'minha-conta') ou base multi-segmento.
  // Pra detectar o `base`, busca por slug no `sourceLocale` em vez de hardcoded.
  const first = segments[0];
  // DYNAMIC_BASES está chaveado por slug PT-BR. Pra outros idiomas, resolvemos
  // o routeId fonte primeiro e mapeamos pro PT slug equivalente.
  let baseLookupKey = first;
  if (sourceLocale !== 'pt-BR') {
    // 'subscriptions' (EN) → routeId 'admin-base' não casa porque base é
    // 'admin' em todos idiomas (mesmo slug). Mas 'pools' EN == 'pools' PT,
    // 'competitions' EN → 'competicoes' PT. Resolvemos via ROUTE_SLUGS:
    // se first casa com algum entry top-level no sourceLocale e tem PT-BR
    // mapping, usa o slug PT-BR como chave em DYNAMIC_BASES.
    const baseRouteIdsByPT = Object.entries(DYNAMIC_BASES).map(([ptSlug, cfg]) => ({
      ptSlug, baseId: cfg.baseId,
    }));
    for (const { ptSlug, baseId } of baseRouteIdsByPT) {
      if (ROUTE_SLUGS[baseId]?.[sourceLocale] === first) {
        baseLookupKey = ptSlug;
        break;
      }
    }
  }
  const base = DYNAMIC_BASES[baseLookupKey];

  if (base) {
    // Multi-segmento: traduz a base, preserva IDs numéricos, traduz sub-slugs.
    const baseSlug = ROUTE_SLUGS[base.baseId]?.[locale];
    if (baseSlug === undefined) return null;
    const subMap = DYNAMIC_SUB_MAPS[base.subPrefix];
    const translated: string[] = [baseSlug];
    for (const seg of segments.slice(1)) {
      // Procura o sub-slug NO IDIOMA FONTE (não hardcoded pt-BR), pra
      // cobrir trocas EN→PT, ES→PT, EN→ES, ES→EN.
      const subRouteId = subMap[sourceLocale][seg];
      if (subRouteId) {
        const localized = ROUTE_SLUGS[subRouteId]?.[locale] ?? seg;
        translated.push(localized);
      } else {
        // ID dinâmico, query path arbitrária, etc — passa intacto
        translated.push(seg);
      }
    }
    const prefix = localePrefix(locale);
    return `${prefix}/${translated.join('/')}${tail}`;
  }

  // Slug top-level: resolve routeId NO IDIOMA FONTE.
  const routeId = routeIdFromSlug(first, sourceLocale);
  if (!routeId) return null;
  const localizedFirst = ROUTE_SLUGS[routeId]?.[locale];
  if (localizedFirst === undefined) return null;
  const rest = segments.slice(1).join('/');
  const prefix = localePrefix(locale);
  const path = rest ? `${prefix}/${localizedFirst}/${rest}` : `${prefix}/${localizedFirst}` || '/';
  return `${path}${tail}`;
}
